import React, { createContext, useContext, ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export type Language = 'pt' | 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  pt: {
    'nav.home': 'Início',
    'nav.products': 'Produtos',
    'nav.about': 'Sobre',
    'nav.blog': 'Blog',
    'nav.careers': 'Trabalhe Conosco',
    'nav.suppliers': 'Fornecedores',
    'nav.contact': 'SAC',
    'nav.portal': 'Portal',
    'hero.title': 'Soluções em Cal Virgem e Cal Hidratada para Toda Indústria Brasileira',
    'hero.subtitle': 'Referência nacional na comercialização de cal virgem e cal hidratada, com suporte técnico e entregas pontuais em todo o Brasil.',
    'hero.cta': 'Solicite um Orçamento',
    'hero.differentials': 'Conheça nossos diferenciais',
    'hero.download_specs': 'Baixar especificações',
    'home.seo.title': 'Cal Virgem e Cal Hidratada para Indústria | Quimical',
    'home.seo.description': 'Fornecedor brasileiro de cal virgem e cal hidratada para siderurgia, construção e saneamento, com suporte técnico e logística nacional.',
    'home.intro.title': 'Cal virgem industrial e cal hidratada para siderurgia, construção e saneamento',
    'home.intro.description': 'Fornecemos cal virgem calcítica e dolomítica (CaO/MgO) e cal hidratada de alta reatividade para processos metalúrgicos, tratamento de água e controle de pH, com suporte técnico e logística nacional.',
    'home.map_section.title': 'Localização Estratégica',
    'home.map_section.subtitle': 'Competitividade logística com plantas distribuídas estrategicamente',
    'home.loading.generic': 'Carregando...',
    'home.loading.contact_form': 'Carregando formulário de contato...',
    'google.rating_title': '⭐ 4,9 de 5 estrelas no Google',
    'google.rating_description': 'Mais de 500 clientes atendidos pela Quimical',
    'google.rating_highlight': 'Baseado em avaliações reais no Google',
    'products.title': 'Nossos Produtos',
    'products.subtitle': 'Soluções completas em cal virgem e cal hidratada',
    'products.download_spec': 'Baixar Especificação',
    'products.request_quote': 'Solicitar Orçamento',
    'productsMenu.badge': '🏭 Cal Virgem & Cal Hidratada Premium',
    'productsMenu.subtitle_primary': 'Cal virgem calcítica e dolomítica, além de cal hidratada industrial com alto padrão de pureza.',
    'productsMenu.subtitle_secondary': 'Especificações técnicas rigorosas para indústrias exigentes.',
    'productsMenu.cta_highlight': '⚡ Solicite cotação rápida',
    'productsMenu.cta_description': 'Entrega em todo Brasil',
    'productsMenu.image_caption': 'Imagem da embalagem meramente ilustrativa.',
    'productsMenu.sections.specs': 'Especificações Químicas',
    'productsMenu.sections.applications': 'Principais Aplicações',
    'productsMenu.download_spec_aria': 'Baixar especificação',
    'productsMenu.disclaimer': 'Os valores indicados são apenas referenciais. A garantia de qualidade é estabelecida conforme a especificação definida pelo cliente e atendida de acordo com suas necessidades.',
    'productsMenu.quote_message_prefix': 'Olá, gostaria de solicitar uma cotação para',
    'productsMenu.quote_message_suffix': 'Podem me ajudar?',
    'productsMenu.items.calciticaVirgem.fullName': 'Cal Virgem Calcítica',
    'productsMenu.items.calciticaVirgem.description': 'Óxido de cálcio de alta pureza para processos industriais exigentes',
    'productsMenu.items.calciticaVirgem.applications.0': 'Siderurgia',
    'productsMenu.items.calciticaVirgem.applications.1': 'Indústrias químicas',
    'productsMenu.items.calciticaVirgem.applications.2': 'Neutralização',
    'productsMenu.items.calciticaVirgem.applications.3': 'Carbeto de cálcio',
    'productsMenu.items.calciticaVirgem.image_alt': 'Embalagem Cal Virgem Calcítica',
    'productsMenu.items.calciticaHidratada.fullName': 'Cal Hidratada Calcítica',
    'productsMenu.items.calciticaHidratada.description': 'Hidróxido de cálcio premium para construção civil e aplicações técnicas',
    'productsMenu.items.calciticaHidratada.applications.0': 'Construção civil',
    'productsMenu.items.calciticaHidratada.applications.1': 'Água potável',
    'productsMenu.items.calciticaHidratada.applications.2': 'Estabilização',
    'productsMenu.items.calciticaHidratada.applications.3': 'Alimentícia E526',
    'productsMenu.items.calciticaHidratada.image_alt': 'Embalagem Cal Hidratada Calcítica',
    'productsMenu.items.dolomiticaVirgem.fullName': 'Cal Virgem Dolomítica',
    'productsMenu.items.dolomiticaVirgem.description': 'Cal virgem dolomítica especializada com alto teor de magnésio para aplicações refratárias',
    'productsMenu.items.dolomiticaVirgem.applications.0': 'Refratários básicos',
    'productsMenu.items.dolomiticaVirgem.applications.1': 'Dessulfuração',
    'productsMenu.items.dolomiticaVirgem.applications.2': 'Ferro-ligas',
    'productsMenu.items.dolomiticaVirgem.applications.3': 'Fertilizantes Mg',
    'productsMenu.items.dolomiticaVirgem.image_alt': 'Embalagem Cal Virgem Dolomítica',
    'productsMenu.items.dolomiticaHidratada.fullName': 'Cal Hidratada Dolomítica',
    'productsMenu.items.dolomiticaHidratada.description': 'Cal hidratada dolomítica com propriedades magnesianas especiais para tratamentos avançados',
    'productsMenu.items.dolomiticaHidratada.applications.0': 'Efluentes industriais',
    'productsMenu.items.dolomiticaHidratada.applications.1': 'Agricultura',
    'productsMenu.items.dolomiticaHidratada.applications.2': 'Farmacêutica',
    'productsMenu.items.dolomiticaHidratada.applications.3': 'Aditivos especiais',
    'productsMenu.items.dolomiticaHidratada.image_alt': 'Embalagem Cal Hidratada Dolomítica',
    'segments.title': 'Segmentos Atendidos',
    'segments.subtitle': 'Fornecemos soluções especializadas para diversos setores industriais, sempre com foco na qualidade e performance.',
    'segments.cta': 'Não encontrou seu segmento?',
    'segments.cta_description': 'Nossa equipe técnica especializada pode desenvolver soluções customizadas para suas necessidades específicas.',
    'segments.cta_button': 'Fale com o Especialista',
    'segments.items.steel.name': 'Siderurgia e Metalurgia',
    'segments.items.steel.description': 'Cal virgem CaO e MgO para dessulfuração, escorificação e produção de aços especiais. Nossa cal virgem calcítica e dolomítica oferece alta reatividade para processos siderúrgicos de precisão.',
    'segments.items.construction.name': 'Construção Civil',
    'segments.items.construction.description': 'Cal hidratada Ca(OH)₂ premium para argamassas, concretos, pinturas e estabilização de solos. Produtos com finura controlada e estabilidade volumétrica garantida.',
    'segments.items.water.name': 'Saneamento e Tratamento de Água',
    'segments.items.water.description': 'Cal virgem e cal hidratada para correção de pH, coagulação, floculação e desinfecção. Soluções específicas para água potável e tratamento de efluentes industriais.',
    'segments.items.pulp.name': 'Papel e Celulose',
    'segments.items.pulp.description': 'Cal virgem calcítica para caustificação do licor verde e cal hidratada para controle de pH. Produtos que atendem aos mais rigorosos padrões da indústria de papel.',
    'segments.items.chemical.name': 'Indústrias Químicas e Petroquímicas',
    'segments.items.chemical.description': 'Cal virgem e cal hidratada como reagentes em sínteses químicas, neutralização de ácidos e produção de compostos cálcicos. Alta pureza para processos exigentes.',
    'segments.items.environment.name': 'Tratamento Ambiental e Sustentabilidade',
    'segments.items.environment.description': 'Soluções com cal para remediação de solos contaminados, neutralização de resíduos ácidos e controle de emissões. Tecnologia verde com cal virgem e cal hidratada.',
    'differentials.title': 'Diferenciais',
    'differentials.brand': 'Quimical',
    'differentials.subtitle': 'O que nos torna a escolha preferencial de indústrias que exigem excelência técnica e confiabilidade.',
    'differentials.why_choose': 'Por que escolher a Quimical?',
    'differentials.with_us': 'Com a Quimical',
    'differentials.conventional': 'Fornecedores convencionais',
    'differentials.items.high_purity.title': 'Alta Pureza em Cal Virgem e Hidratada',
    'differentials.items.high_purity.description': 'Concentração superior que resulta em economia real para nossos clientes',
    'differentials.items.quality_control.title': 'Controle de Qualidade Rigoroso',
    'differentials.items.quality_control.description': 'Padrões internacionais garantem consistência em todos os nossos produtos',
    'differentials.items.punctuality.title': 'Pontualidade nas Entregas',
    'differentials.items.punctuality.description': 'Logística otimizada e parceiros estratégicos para atendimento contínuo',
    'differentials.items.technical_support.title': 'Suporte Técnico Especializado',
    'differentials.items.technical_support.description': 'Experientes profissionais da área química dedicados ao seu sucesso',
    'differentials.items.trust.title': 'Confiança e Credibilidade',
    'differentials.items.trust.description': 'Histórico sólido de relacionamentos duradouros com nossos clientes',
    'differentials.items.logistics.title': 'Competitividade Logística',
    'differentials.items.logistics.description': 'Rede de plantas estrategicamente posicionadas para otimizar custos',
    'differentials.with_us_items.0': 'Cal virgem e cal hidratada de alta pureza',
    'differentials.with_us_items.1': 'Qualidade consistente e controlada',
    'differentials.with_us_items.2': 'Suporte técnico especializado',
    'differentials.with_us_items.3': 'Pontualidade garantida nas entregas',
    'differentials.conventional_items.0': 'Variação na pureza do CaO',
    'differentials.conventional_items.1': 'Controle de qualidade limitado',
    'differentials.conventional_items.2': 'Suporte técnico básico',
    'differentials.conventional_items.3': 'Atrasos frequentes na entrega',
    'sustainability.title': 'Compromisso com a',
    'sustainability.title_highlight': 'Sustentabilidade',
    'sustainability.subtitle': 'Nossa produção de cal virgem e cal hidratada utiliza 70% de energia renovável através de biomassa, reduzindo drasticamente nossa pegada de carbono e promovendo sustentabilidade industrial.',
    'sustainability.commitment': 'Nosso Compromisso Ambiental',
    'sustainability.results': 'Resultados Mensuráveis',
    'sustainability.initiatives.renewable_energy.title': 'Energia Renovável (Biomassa)',
    'sustainability.initiatives.renewable_energy.description': 'Utilizamos biomassa como fonte de energia renovável, reduzindo significativamente nossa pegada de carbono na produção de cal virgem e cal hidratada.',
    'sustainability.initiatives.circular_economy.title': 'Economia Circular',
    'sustainability.initiatives.circular_economy.description': 'Reaproveitamento total de subprodutos da cal e integração com outros processos industriais, maximizando eficiência e minimizando desperdícios.',
    'sustainability.initiatives.energy_efficiency.title': 'Eficiência Energética Avançada',
    'sustainability.initiatives.energy_efficiency.description': 'Tecnologias de ponta para otimização térmica nos fornos, reduzindo consumo energético por tonelada de cal virgem produzida.',
    'sustainability.initiatives.environmental_management.title': 'Gestão Ambiental Certificada',
    'sustainability.initiatives.environmental_management.description': 'Certificações ambientais rigorosas e monitoramento contínuo de todas as emissões, garantindo conformidade e melhoria contínua.',
    'sustainability.commitment_points.biomass': 'Biomassa como combustível: 70% da energia utilizada na produção de cal virgem provém de biomassa renovável.',
    'sustainability.commitment_points.co2': 'Redução de CO₂: diminuição de 40% nas emissões de carbono comparado a combustíveis fósseis tradicionais.',
    'sustainability.commitment_points.clean_technology': 'Tecnologia limpa: filtros avançados e controle de emissões para produção sustentável de cal hidratada.',
    'sustainability.results.co2_label': 'CO₂ com biomassa',
    'sustainability.results.renewable_energy_label': 'Energia renovável',
    'sustainability.results.waste_reuse_label': 'Reuso de resíduos',
    'sustainability.results.licensing_label': 'Licenciamento',
    'map.plants_title': 'Nossas Plantas Produtivas',
    'map.plants_description': 'Competitividade logística em cal virgem e cal hidratada em todo o Brasil',
    'map.button.loading': 'Carregando mapa...',
    'map.button.show': 'Ver mapa interativo',
    'map.unsupported': 'Mapa interativo não suportado neste navegador. Informações de localização exibidas acima.',
    'map.loading_message': 'Carregando mapa interativo...',
    'map.hide': 'Ocultar mapa',
    'contact.title': 'Entre em Contato',
    'contact.subtitle': 'Nossa equipe especializada está pronta para atender suas necessidades específicas. Solicite um orçamento ou tire suas dúvidas técnicas.',
    'contact.form_title': 'Solicite um Orçamento',
    'contact.name': 'Nome completo',
    'contact.email': 'E-mail',
    'contact.company': 'Empresa',
    'contact.phone': 'Telefone',
    'contact.message': 'Mensagem',
    'contact.submit': 'Enviar Solicitação',
    'contact.required': 'Campos obrigatórios. Responderemos em até 24 horas.',
    'contact.quick_support': 'Atendimento Rápido',
    'contact.quick_support_description': 'Fale diretamente com nossos especialistas via WhatsApp para dúvidas técnicas urgentes.',
    'contact.whatsapp': 'WhatsApp Técnico',
    'contact.locations': 'Nossas Plantas Produtivas',
    'contact.locations_description': 'Localização privilegiada para máxima competitividade logística',
    'contact.info.customer_service': 'Central de Atendimento',
    'contact.info.email': 'E-mail',
    'contact.info.address': 'Endereço Principal',
    'contact.phone_value': '0800 591 7215',
    'contact.email_value': 'cal@quimical.com.br',
    'cookie.title': 'Uso de Cookies',
    'cookie.description': 'Utilizamos cookies para melhorar sua experiência em nosso site.',
    'cookie.consent_prompt': 'Ao continuar navegando, você concorda com nossa',
    'cookie.policy_link': 'Política de Cookies',
    'cookie.accept': 'Aceitar',
    'cookie.decline': 'Recusar',
    'cookie.close': 'Fechar aviso de cookies',
    'footer.navigation': 'Navegação',
    'footer.connect': 'Conecte-se Conosco',
    'footer.products.calcitica_quicklime': 'CaO - Cal Virgem Calcítica',
    'footer.products.calcitica_hydrated': 'Ca(OH)₂ - Cal Hidratada Calcítica',
    'footer.products.dolomitic_quicklime': 'MgO - Cal Virgem Dolomítica',
    'footer.products.dolomitic_hydrated': 'Mg(OH)₂ - Cal Hidratada Dolomítica',
    'footer.plants.title': 'Fábricas nos municípios',
    'footer.plants.arcos': 'Arcos, Minas Gerais',
    'footer.plants.corrego_fundo': 'Córrego Fundo, Minas Gerais',
    'footer.plants.pains': 'Pains, Minas Gerais',
    'footer.plants.sao_jose_lapa': 'São José da Lapa, Minas Gerais',
    'footer.plants.colombo': 'Colombo, Paraná',
    'footer.plants.almirante_tamandare': 'Almirante Tamandaré, Paraná',
    'footer.plants.ponta_grossa': 'Ponta Grossa, Paraná',
    'footer.plants.euclides_cunha': 'Euclides da Cunha, Bahia',
    'footer.plants.apodi': 'Apodi, Rio Grande do Norte',
    'footer.address': 'Rua Belo Horizonte, 6 Santo Antônio - Arcos/MG 35599-170',
    'footer.legal.privacy': 'Política de Privacidade',
    'footer.legal.terms': 'Termos de Uso',
    'footer.legal.cookies': 'Uso de Cookies',
    'footer.copyright': '2026 Quimical é uma marca Empresa Brasileira de Mercadologia LTDA CNPJ 10.403.259/0001-37',
    'footer.version': 'Versão 1.1.9'
  },
  en: {
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.about': 'About',
    'nav.blog': 'Blog',
    'nav.careers': 'Careers',
    'nav.suppliers': 'Suppliers',
    'nav.contact': 'Contact',
    'nav.portal': 'Portal',
    'hero.title': 'Quicklime and Hydrated Lime Solutions for All Brazilian Industry',
    'hero.subtitle': 'National reference in the distribution of quicklime and hydrated lime, with technical support and punctual deliveries throughout Brazil.',
    'hero.cta': 'Request a Quote',
    'hero.differentials': 'See our advantages',
    'hero.download_specs': 'Download specifications',
    'home.seo.title': 'Quicklime and Hydrated Lime for Industry | Quimical',
    'home.seo.description': 'Brazilian supplier of quicklime and hydrated lime for steel, construction, and sanitation, with technical support and nationwide logistics.',
    'home.intro.title': 'Industrial quicklime and hydrated lime for steel, construction, and sanitation',
    'home.intro.description': 'We supply calcitic and dolomitic quicklime (CaO/MgO) and high-reactivity hydrated lime for metallurgical processes, water treatment, and pH control, backed by technical support and national logistics.',
    'home.map_section.title': 'Strategic Location',
    'home.map_section.subtitle': 'Logistical competitiveness with plants strategically distributed',
    'home.loading.generic': 'Loading...',
    'home.loading.contact_form': 'Loading contact form...',
    'google.rating_title': '⭐ 4.9 out of 5 stars on Google',
    'google.rating_description': 'Over 500 clients served by Quimical',
    'google.rating_highlight': 'Based on real Google reviews',
    'products.title': 'Our Products',
    'products.subtitle': 'Complete solutions in quicklime, hydrated lime and limestone',
    'products.download_spec': 'Download Specification',
    'products.request_quote': 'Request a Quote',
    'productsMenu.badge': '🏭 Premium Quicklime & Hydrated Lime',
    'productsMenu.subtitle_primary': 'Calcitic and dolomitic quicklime plus industrial-grade hydrated lime with high purity.',
    'productsMenu.subtitle_secondary': 'Rigorous technical specifications for demanding industries.',
    'productsMenu.cta_highlight': '⚡ Request a fast quote',
    'productsMenu.cta_description': 'Delivery throughout Brazil',
    'productsMenu.image_caption': 'Product packaging image for illustration purposes only.',
    'productsMenu.sections.specs': 'Chemical Specifications',
    'productsMenu.sections.applications': 'Main Applications',
    'productsMenu.download_spec_aria': 'Download specification',
    'productsMenu.disclaimer': 'The values shown are for reference only. Quality assurance is established according to the specification defined by the client and fulfilled according to their needs.',
    'productsMenu.quote_message_prefix': 'Hello, I would like to request a quote for',
    'productsMenu.quote_message_suffix': 'Could you help me?',
    'productsMenu.items.calciticaVirgem.fullName': 'Calcitic Quicklime',
    'productsMenu.items.calciticaVirgem.description': 'High-purity calcium oxide for demanding industrial processes',
    'productsMenu.items.calciticaVirgem.applications.0': 'Steelmaking',
    'productsMenu.items.calciticaVirgem.applications.1': 'Chemical industries',
    'productsMenu.items.calciticaVirgem.applications.2': 'Neutralization',
    'productsMenu.items.calciticaVirgem.applications.3': 'Calcium carbide',
    'productsMenu.items.calciticaVirgem.image_alt': 'Calcitic quicklime packaging',
    'productsMenu.items.calciticaHidratada.fullName': 'Calcitic Hydrated Lime',
    'productsMenu.items.calciticaHidratada.description': 'Premium calcium hydroxide for construction and technical applications',
    'productsMenu.items.calciticaHidratada.applications.0': 'Construction',
    'productsMenu.items.calciticaHidratada.applications.1': 'Drinking water',
    'productsMenu.items.calciticaHidratada.applications.2': 'Stabilization',
    'productsMenu.items.calciticaHidratada.applications.3': 'Food grade E526',
    'productsMenu.items.calciticaHidratada.image_alt': 'Calcitic hydrated lime packaging',
    'productsMenu.items.dolomiticaVirgem.fullName': 'Dolomitic Quicklime',
    'productsMenu.items.dolomiticaVirgem.description': 'Specialized dolomitic quicklime with high magnesium content for refractory applications',
    'productsMenu.items.dolomiticaVirgem.applications.0': 'Basic refractories',
    'productsMenu.items.dolomiticaVirgem.applications.1': 'Desulfurization',
    'productsMenu.items.dolomiticaVirgem.applications.2': 'Ferroalloys',
    'productsMenu.items.dolomiticaVirgem.applications.3': 'Magnesium fertilizers',
    'productsMenu.items.dolomiticaVirgem.image_alt': 'Dolomitic quicklime packaging',
    'productsMenu.items.dolomiticaHidratada.fullName': 'Dolomitic Hydrated Lime',
    'productsMenu.items.dolomiticaHidratada.description': 'Dolomitic hydrated lime with special magnesium properties for advanced treatments',
    'productsMenu.items.dolomiticaHidratada.applications.0': 'Industrial effluents',
    'productsMenu.items.dolomiticaHidratada.applications.1': 'Agriculture',
    'productsMenu.items.dolomiticaHidratada.applications.2': 'Pharmaceuticals',
    'productsMenu.items.dolomiticaHidratada.applications.3': 'Special additives',
    'productsMenu.items.dolomiticaHidratada.image_alt': 'Dolomitic hydrated lime packaging',
    'segments.title': 'Served Segments',
    'segments.subtitle': 'We provide specialized solutions for various industrial sectors, always focusing on quality and performance.',
    'segments.cta': 'Didn\'t find your segment?',
    'segments.cta_description': 'Our specialized technical team can develop customized solutions for your specific needs.',
    'segments.cta_button': 'Talk to the Specialist',
    'segments.items.steel.name': 'Steel and Metallurgy',
    'segments.items.steel.description': 'Quicklime CaO and MgO for desulfurization, slag formation and special steel production. Our calcitic and dolomitic quicklime offers high reactivity for precision steelmaking.',
    'segments.items.construction.name': 'Construction',
    'segments.items.construction.description': 'Premium Ca(OH)₂ hydrated lime for mortars, concrete, coatings and soil stabilization. Products with controlled fineness and guaranteed dimensional stability.',
    'segments.items.water.name': 'Water and Wastewater Treatment',
    'segments.items.water.description': 'Quicklime and hydrated lime for pH correction, coagulation, flocculation and disinfection. Specific solutions for potable water and industrial effluent treatment.',
    'segments.items.pulp.name': 'Pulp and Paper',
    'segments.items.pulp.description': 'Calcitic quicklime for green liquor causticizing and hydrated lime for pH control. Products that meet the most stringent paper industry standards.',
    'segments.items.chemical.name': 'Chemical and Petrochemical Industries',
    'segments.items.chemical.description': 'Quicklime and hydrated lime as reagents in chemical synthesis, acid neutralization and calcium compound production. High purity for demanding processes.',
    'segments.items.environment.name': 'Environmental Treatment and Sustainability',
    'segments.items.environment.description': 'Lime-based solutions for contaminated soil remediation, acidic waste neutralization and emissions control. Green technology with quicklime and hydrated lime.',
    'differentials.title': 'Differentials',
    'differentials.brand': 'Quimical',
    'differentials.subtitle': 'What makes us the preferred choice for industries that demand technical excellence and reliability.',
    'differentials.why_choose': 'Why choose Quimical?',
    'differentials.with_us': 'With Quimical',
    'differentials.conventional': 'Conventional suppliers',
    'differentials.items.high_purity.title': 'High Purity in Quicklime and Hydrated Lime',
    'differentials.items.high_purity.description': 'Superior concentration that delivers real savings for our clients',
    'differentials.items.quality_control.title': 'Strict Quality Control',
    'differentials.items.quality_control.description': 'International standards ensure consistency across all of our products',
    'differentials.items.punctuality.title': 'On-time Deliveries',
    'differentials.items.punctuality.description': 'Optimized logistics and strategic partners for continuous supply',
    'differentials.items.technical_support.title': 'Specialized Technical Support',
    'differentials.items.technical_support.description': 'Experienced chemistry professionals dedicated to your success',
    'differentials.items.trust.title': 'Trust and Credibility',
    'differentials.items.trust.description': 'Solid track record of long-term relationships with our clients',
    'differentials.items.logistics.title': 'Logistics Competitiveness',
    'differentials.items.logistics.description': 'Network of strategically positioned plants to optimize costs',
    'differentials.with_us_items.0': 'High-purity quicklime and hydrated lime',
    'differentials.with_us_items.1': 'Consistent, controlled quality',
    'differentials.with_us_items.2': 'Specialized technical support',
    'differentials.with_us_items.3': 'On-time delivery guaranteed',
    'differentials.conventional_items.0': 'Fluctuating CaO purity',
    'differentials.conventional_items.1': 'Limited quality control',
    'differentials.conventional_items.2': 'Basic technical support',
    'differentials.conventional_items.3': 'Frequent delivery delays',
    'sustainability.title': 'Commitment to',
    'sustainability.title_highlight': 'Sustainability',
    'sustainability.subtitle': 'Our quicklime and hydrated lime production uses 70% renewable energy through biomass, drastically reducing our carbon footprint and promoting industrial sustainability.',
    'sustainability.commitment': 'Our Environmental Commitment',
    'sustainability.results': 'Measurable Results',
    'sustainability.initiatives.renewable_energy.title': 'Renewable Energy (Biomass)',
    'sustainability.initiatives.renewable_energy.description': 'We use biomass as a renewable energy source, significantly reducing our carbon footprint in quicklime and hydrated lime production.',
    'sustainability.initiatives.circular_economy.title': 'Circular Economy',
    'sustainability.initiatives.circular_economy.description': 'Full reuse of lime by-products and integration with other industrial processes, maximizing efficiency and minimizing waste.',
    'sustainability.initiatives.energy_efficiency.title': 'Advanced Energy Efficiency',
    'sustainability.initiatives.energy_efficiency.description': 'Cutting-edge technologies for kiln thermal optimization, reducing energy consumption per ton of quicklime produced.',
    'sustainability.initiatives.environmental_management.title': 'Certified Environmental Management',
    'sustainability.initiatives.environmental_management.description': 'Rigorous environmental certifications and continuous monitoring of emissions ensure compliance and ongoing improvement.',
    'sustainability.commitment_points.biomass': 'Biomass as fuel: 70% of the energy used in quicklime production comes from renewable biomass.',
    'sustainability.commitment_points.co2': 'CO₂ reduction: 40% decrease in carbon emissions compared to traditional fossil fuels.',
    'sustainability.commitment_points.clean_technology': 'Clean technology: advanced filters and emissions control for sustainable hydrated lime production.',
    'sustainability.results.co2_label': 'CO₂ with biomass',
    'sustainability.results.renewable_energy_label': 'Renewable energy',
    'sustainability.results.waste_reuse_label': 'Waste reuse',
    'sustainability.results.licensing_label': 'Licensing',
    'map.plants_title': 'Our Production Plants',
    'map.plants_description': 'Logistical competitiveness in quicklime and hydrated lime across Brazil',
    'map.button.loading': 'Loading map...',
    'map.button.show': 'View interactive map',
    'map.unsupported': 'Interactive map not supported in this browser. Location information shown above.',
    'map.loading_message': 'Loading interactive map...',
    'map.hide': 'Hide map',
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Our specialized team is ready to meet your specific needs. Request a quote or ask your technical questions.',
    'contact.form_title': 'Request a Quote',
    'contact.name': 'Full name',
    'contact.email': 'Email',
    'contact.company': 'Company',
    'contact.phone': 'Phone',
    'contact.message': 'Message',
    'contact.submit': 'Submit Request',
    'contact.required': 'Required fields. We will respond within 24 hours.',
    'contact.quick_support': 'Quick Support',
    'contact.quick_support_description': 'Talk directly with our specialists via WhatsApp for urgent technical questions.',
    'contact.whatsapp': 'Technical WhatsApp',
    'contact.locations': 'Our Production Plants',
    'contact.locations_description': 'Privileged location for maximum logistical competitiveness',
    'contact.info.customer_service': 'Customer Service',
    'contact.info.email': 'Email',
    'contact.info.address': 'Main Address',
    'contact.phone_value': '0800 591 7215',
    'contact.email_value': 'cal@quimical.com.br',
    'cookie.title': 'Use of Cookies',
    'cookie.description': 'We use cookies to improve your experience on our website.',
    'cookie.consent_prompt': 'By continuing to browse, you agree to our',
    'cookie.policy_link': 'Cookie Policy',
    'cookie.accept': 'Accept',
    'cookie.decline': 'Decline',
    'cookie.close': 'Close cookie notice',
    'footer.navigation': 'Navigation',
    'footer.connect': 'Connect with Us',
    'footer.products.calcitica_quicklime': 'CaO - Calcitic Quicklime',
    'footer.products.calcitica_hydrated': 'Ca(OH)₂ - Calcitic Hydrated Lime',
    'footer.products.dolomitic_quicklime': 'MgO - Dolomitic Quicklime',
    'footer.products.dolomitic_hydrated': 'Mg(OH)₂ - Dolomitic Hydrated Lime',
    'footer.plants.title': 'Factories in the municipalities',
    'footer.plants.arcos': 'Arcos, Minas Gerais',
    'footer.plants.corrego_fundo': 'Córrego Fundo, Minas Gerais',
    'footer.plants.pains': 'Pains, Minas Gerais',
    'footer.plants.sao_jose_lapa': 'São José da Lapa, Minas Gerais',
    'footer.plants.colombo': 'Colombo, Paraná',
    'footer.plants.almirante_tamandare': 'Almirante Tamandaré, Paraná',
    'footer.plants.ponta_grossa': 'Ponta Grossa, Paraná',
    'footer.plants.euclides_cunha': 'Euclides da Cunha, Bahia',
    'footer.plants.apodi': 'Apodi, Rio Grande do Norte',
    'footer.address': 'Rua Belo Horizonte, 6 Santo Antônio - Arcos/MG 35599-170',
    'footer.legal.privacy': 'Privacy Policy',
    'footer.legal.terms': 'Terms of Use',
    'footer.legal.cookies': 'Cookie Policy',
    'footer.copyright': '2026 Quimical is a brand of Empresa Brasileira de Mercadologia LTDA CNPJ 10.403.259/0001-37',
    'footer.version': 'Version 1.1.9'
  },
  es: {
    'nav.home': 'Inicio',
    'nav.products': 'Productos',
    'nav.about': 'Acerca',
    'nav.blog': 'Blog',
    'nav.careers': 'Trabaja con Nosotros',
    'nav.suppliers': 'Proveedores',
    'nav.contact': 'Contacto',
    'nav.portal': 'Portal',
    'hero.title': 'Soluciones en Cal Viva y Cal Hidratada para Toda la Industria Brasileña',
    'hero.subtitle': 'Referencia nacional en distribución de cal viva y cal hidratada, con soporte técnico y entregas puntuales en todo Brasil.',
    'hero.cta': 'Solicitar Cotización',
    'hero.differentials': 'Conozca nuestros diferenciales',
    'hero.download_specs': 'Descargar especificaciones',
    'home.seo.title': 'Cal viva y cal hidratada para la industria | Quimical',
    'home.seo.description': 'Proveedor brasileño de cal viva y cal hidratada para siderurgia, construcción y saneamiento, con soporte técnico y logística nacional.',
    'home.intro.title': 'Cal viva industrial y cal hidratada para siderurgia, construcción y saneamiento',
    'home.intro.description': 'Suministramos cal viva calcítica y dolomítica (CaO/MgO) y cal hidratada de alta reactividad para procesos metalúrgicos, tratamiento de agua y control de pH, con soporte técnico y logística nacional.',
    'home.map_section.title': 'Ubicación Estratégica',
    'home.map_section.subtitle': 'Competitividad logística con plantas distribuidas estratégicamente',
    'home.loading.generic': 'Cargando...',
    'home.loading.contact_form': 'Cargando formulario de contacto...',
    'google.rating_title': '⭐ 4,9 de 5 estrellas en Google',
    'google.rating_description': 'Más de 500 clientes atendidos por Quimical',
    'google.rating_highlight': 'Basado en reseñas reales de Google',
    'products.title': 'Nuestros Productos',
    'products.subtitle': 'Soluciones completas en cal viva, cal hidratada y calizas',
    'products.download_spec': 'Descargar Especificación',
    'products.request_quote': 'Solicitar Cotización',
    'productsMenu.badge': '🏭 Cal Viva y Cal Hidratada Premium',
    'productsMenu.subtitle_primary': 'Cal viva calcítica y dolomítica, además de cal hidratada industrial con alta pureza.',
    'productsMenu.subtitle_secondary': 'Especificaciones técnicas rigurosas para industrias exigentes.',
    'productsMenu.cta_highlight': '⚡ Solicite una cotización rápida',
    'productsMenu.cta_description': 'Entrega en todo Brasil',
    'productsMenu.image_caption': 'Imagen del empaque meramente ilustrativa.',
    'productsMenu.sections.specs': 'Especificaciones Químicas',
    'productsMenu.sections.applications': 'Principales Aplicaciones',
    'productsMenu.download_spec_aria': 'Descargar especificación',
    'productsMenu.disclaimer': 'Los valores indicados son solo de referencia. La garantía de calidad se establece según la especificación definida por el cliente y se atiende de acuerdo con sus necesidades.',
    'productsMenu.quote_message_prefix': 'Hola, me gustaría solicitar una cotización para',
    'productsMenu.quote_message_suffix': '¿Pueden ayudarme?',
    'productsMenu.items.calciticaVirgem.fullName': 'Cal Viva Calcítica',
    'productsMenu.items.calciticaVirgem.description': 'Óxido de calcio de alta pureza para procesos industriales exigentes',
    'productsMenu.items.calciticaVirgem.applications.0': 'Siderurgia',
    'productsMenu.items.calciticaVirgem.applications.1': 'Industrias químicas',
    'productsMenu.items.calciticaVirgem.applications.2': 'Neutralización',
    'productsMenu.items.calciticaVirgem.applications.3': 'Carburo de calcio',
    'productsMenu.items.calciticaVirgem.image_alt': 'Empaque de cal viva calcítica',
    'productsMenu.items.calciticaHidratada.fullName': 'Cal Hidratada Calcítica',
    'productsMenu.items.calciticaHidratada.description': 'Hidróxido de calcio premium para construcción y aplicaciones técnicas',
    'productsMenu.items.calciticaHidratada.applications.0': 'Construcción',
    'productsMenu.items.calciticaHidratada.applications.1': 'Agua potable',
    'productsMenu.items.calciticaHidratada.applications.2': 'Estabilización',
    'productsMenu.items.calciticaHidratada.applications.3': 'Alimenticio E526',
    'productsMenu.items.calciticaHidratada.image_alt': 'Empaque de cal hidratada calcítica',
    'productsMenu.items.dolomiticaVirgem.fullName': 'Cal Viva Dolomítica',
    'productsMenu.items.dolomiticaVirgem.description': 'Cal viva dolomítica especializada con alto contenido de magnesio para aplicaciones refractarias',
    'productsMenu.items.dolomiticaVirgem.applications.0': 'Refractarios básicos',
    'productsMenu.items.dolomiticaVirgem.applications.1': 'Desulfuración',
    'productsMenu.items.dolomiticaVirgem.applications.2': 'Ferroaleaciones',
    'productsMenu.items.dolomiticaVirgem.applications.3': 'Fertilizantes con Mg',
    'productsMenu.items.dolomiticaVirgem.image_alt': 'Empaque de cal viva dolomítica',
    'productsMenu.items.dolomiticaHidratada.fullName': 'Cal Hidratada Dolomítica',
    'productsMenu.items.dolomiticaHidratada.description': 'Cal hidratada dolomítica con propiedades magnesianas especiales para tratamientos avanzados',
    'productsMenu.items.dolomiticaHidratada.applications.0': 'Efluentes industriales',
    'productsMenu.items.dolomiticaHidratada.applications.1': 'Agricultura',
    'productsMenu.items.dolomiticaHidratada.applications.2': 'Farmacéutica',
    'productsMenu.items.dolomiticaHidratada.applications.3': 'Aditivos especiales',
    'productsMenu.items.dolomiticaHidratada.image_alt': 'Empaque de cal hidratada dolomítica',
    'segments.title': 'Segmentos Atendidos',
    'segments.subtitle': 'Proporcionamos soluciones especializadas para diversos sectores industriales, siempre enfocados en la calidad y el rendimiento.',
    'segments.cta': '¿No encontró su segmento?',
    'segments.cta_description': 'Nuestro equipo técnico especializado puede desarrollar soluciones personalizadas para sus necesidades específicas.',
    'segments.cta_button': 'Hable con el Especialista',
    'segments.items.steel.name': 'Siderurgia y Metalurgia',
    'segments.items.steel.description': 'Cal viva CaO y MgO para desulfuración, escorificación y producción de aceros especiales. Nuestra cal viva calcítica y dolomítica ofrece alta reactividad para procesos siderúrgicos de precisión.',
    'segments.items.construction.name': 'Construcción',
    'segments.items.construction.description': 'Cal hidratada Ca(OH)₂ premium para morteros, concretos, revestimientos y estabilización de suelos. Productos con finura controlada y estabilidad volumétrica garantizada.',
    'segments.items.water.name': 'Saneamiento y Tratamiento de Agua',
    'segments.items.water.description': 'Cal viva y cal hidratada para corrección de pH, coagulación, floculación y desinfección. Soluciones específicas para agua potable y tratamiento de efluentes industriales.',
    'segments.items.pulp.name': 'Papel y Celulosa',
    'segments.items.pulp.description': 'Cal viva calcítica para la caustificación del licor verde y cal hidratada para el control de pH. Productos que cumplen los estándares más exigentes de la industria del papel.',
    'segments.items.chemical.name': 'Industrias Químicas y Petroquímicas',
    'segments.items.chemical.description': 'Cal viva y cal hidratada como reactivos en síntesis químicas, neutralización de ácidos y producción de compuestos cálcicos. Alta pureza para procesos exigentes.',
    'segments.items.environment.name': 'Tratamiento Ambiental y Sostenibilidad',
    'segments.items.environment.description': 'Soluciones con cal para remediación de suelos contaminados, neutralización de residuos ácidos y control de emisiones. Tecnología verde con cal viva y cal hidratada.',
    'differentials.title': 'Diferenciales',
    'differentials.brand': 'Quimical',
    'differentials.subtitle': 'Lo que nos convierte en la opción preferida de industrias que exigen excelencia técnica y confiabilidad.',
    'differentials.why_choose': '¿Por qué elegir Quimical?',
    'differentials.with_us': 'Con Quimical',
    'differentials.conventional': 'Proveedores convencionales',
    'differentials.items.high_purity.title': 'Alta Pureza en Cal Viva y Cal Hidratada',
    'differentials.items.high_purity.description': 'Concentración superior que genera ahorros reales para nuestros clientes',
    'differentials.items.quality_control.title': 'Control de Calidad Rigoroso',
    'differentials.items.quality_control.description': 'Estándares internacionales garantizan consistencia en todos nuestros productos',
    'differentials.items.punctuality.title': 'Puntualidad en las Entregas',
    'differentials.items.punctuality.description': 'Logística optimizada y socios estratégicos para un suministro continuo',
    'differentials.items.technical_support.title': 'Soporte Técnico Especializado',
    'differentials.items.technical_support.description': 'Profesionales experimentados en química dedicados a su éxito',
    'differentials.items.trust.title': 'Confianza y Credibilidad',
    'differentials.items.trust.description': 'Historial sólido de relaciones duraderas con nuestros clientes',
    'differentials.items.logistics.title': 'Competitividad Logística',
    'differentials.items.logistics.description': 'Red de plantas estratégicamente ubicadas para optimizar costos',
    'differentials.with_us_items.0': 'Cal viva y cal hidratada de alta pureza',
    'differentials.with_us_items.1': 'Calidad consistente y controlada',
    'differentials.with_us_items.2': 'Soporte técnico especializado',
    'differentials.with_us_items.3': 'Entregas puntuales garantizadas',
    'differentials.conventional_items.0': 'Variación en la pureza del CaO',
    'differentials.conventional_items.1': 'Control de calidad limitado',
    'differentials.conventional_items.2': 'Soporte técnico básico',
    'differentials.conventional_items.3': 'Retrasos frecuentes en la entrega',
    'sustainability.title': 'Compromiso con la',
    'sustainability.title_highlight': 'Sostenibilidad',
    'sustainability.subtitle': 'Nuestra producción de cal viva y cal hidratada utiliza 70% de energía renovable a través de biomasa, reduciendo drásticamente nuestra huella de carbono y promoviendo la sostenibilidad industrial.',
    'sustainability.commitment': 'Nuestro Compromiso Ambiental',
    'sustainability.results': 'Resultados Medibles',
    'sustainability.initiatives.renewable_energy.title': 'Energía Renovable (Biomasa)',
    'sustainability.initiatives.renewable_energy.description': 'Utilizamos biomasa como fuente de energía renovable, reduciendo significativamente nuestra huella de carbono en la producción de cal viva y cal hidratada.',
    'sustainability.initiatives.circular_economy.title': 'Economía Circular',
    'sustainability.initiatives.circular_economy.description': 'Reaprovechamiento total de los subproductos de la cal e integración con otros procesos industriales, maximizando la eficiencia y minimizando los desperdicios.',
    'sustainability.initiatives.energy_efficiency.title': 'Eficiencia Energética Avanzada',
    'sustainability.initiatives.energy_efficiency.description': 'Tecnologías de vanguardia para la optimización térmica de los hornos, reduciendo el consumo energético por tonelada de cal viva producida.',
    'sustainability.initiatives.environmental_management.title': 'Gestión Ambiental Certificada',
    'sustainability.initiatives.environmental_management.description': 'Certificaciones ambientales rigurosas y monitoreo continuo de todas las emisiones, garantizando conformidad y mejora continua.',
    'sustainability.commitment_points.biomass': 'Biomasa como combustible: 70% de la energía utilizada en la producción de cal viva proviene de biomasa renovable.',
    'sustainability.commitment_points.co2': 'Reducción de CO₂: disminución del 40% en las emisiones de carbono en comparación con combustibles fósiles tradicionales.',
    'sustainability.commitment_points.clean_technology': 'Tecnología limpia: filtros avanzados y control de emisiones para una producción sostenible de cal hidratada.',
    'sustainability.results.co2_label': 'CO₂ con biomasa',
    'sustainability.results.renewable_energy_label': 'Energía renovable',
    'sustainability.results.waste_reuse_label': 'Reutilización de residuos',
    'sustainability.results.licensing_label': 'Licenciamiento',
    'map.plants_title': 'Nuestras Plantas Productivas',
    'map.plants_description': 'Competitividad logística en cal viva y cal hidratada en todo Brasil',
    'map.button.loading': 'Cargando mapa...',
    'map.button.show': 'Ver mapa interactivo',
    'map.unsupported': 'Mapa interativo no soportado neste navegador. Información de ubicación mostrada arriba.',
    'map.loading_message': 'Cargando mapa interactivo...',
    'map.hide': 'Ocultar mapa',
    'contact.title': 'Contáctanos',
    'contact.subtitle': 'Nuestro equipo especializado está listo para atender sus necesidades específicas. Solicite una cotización o haga sus preguntas técnicas.',
    'contact.form_title': 'Solicitar Cotización',
    'contact.name': 'Nombre completo',
    'contact.email': 'Correo electrónico',
    'contact.company': 'Empresa',
    'contact.phone': 'Teléfono',
    'contact.message': 'Mensaje',
    'contact.submit': 'Enviar Solicitud',
    'contact.required': 'Campos obligatorios. Responderemos en 24 horas.',
    'contact.quick_support': 'Atención Rápida',
    'contact.quick_support_description': 'Hable directamente con nuestros especialistas vía WhatsApp para dudas técnicas urgentes.',
    'contact.whatsapp': 'WhatsApp Técnico',
    'contact.locations': 'Nuestras Plantas Productivas',
    'contact.locations_description': 'Ubicación privilegiada para máxima competitividad logística',
    'contact.info.customer_service': 'Atención al Cliente',
    'contact.info.email': 'Correo Electrónico',
    'contact.info.address': 'Dirección Principal',
    'contact.phone_value': '0800 591 7215',
    'contact.email_value': 'cal@quimical.com.br',
    'cookie.title': 'Uso de Cookies',
    'cookie.description': 'Utilizamos cookies para mejorar su experiencia en nuestro sitio web.',
    'cookie.consent_prompt': 'Al continuar navegando, usted acepta nuestra',
    'cookie.policy_link': 'Política de Cookies',
    'cookie.accept': 'Aceptar',
    'cookie.decline': 'Rechazar',
    'cookie.close': 'Cerrar aviso de cookies',
    'footer.navigation': 'Navegación',
    'footer.connect': 'Conéctate con Nosotros',
    'footer.products.calcitica_quicklime': 'CaO - Cal Viva Calcítica',
    'footer.products.calcitica_hydrated': 'Ca(OH)₂ - Cal Hidratada Calcítica',
    'footer.products.dolomitic_quicklime': 'MgO - Cal Viva Dolomítica',
    'footer.products.dolomitic_hydrated': 'Mg(OH)₂ - Cal Hidratada Dolomítica',
    'footer.plants.title': 'Fábricas en los municipios',
    'footer.plants.arcos': 'Arcos, Minas Gerais',
    'footer.plants.corrego_fundo': 'Córrego Fundo, Minas Gerais',
    'footer.plants.pains': 'Pains, Minas Gerais',
    'footer.plants.sao_jose_lapa': 'São José da Lapa, Minas Gerais',
    'footer.plants.colombo': 'Colombo, Paraná',
    'footer.plants.almirante_tamandare': 'Almirante Tamandaré, Paraná',
    'footer.plants.ponta_grossa': 'Ponta Grossa, Paraná',
    'footer.plants.euclides_cunha': 'Euclides da Cunha, Bahia',
    'footer.plants.apodi': 'Apodi, Rio Grande do Norte',
    'footer.address': 'Rua Belo Horizonte, 6 Santo Antônio - Arcos/MG 35599-170',
    'footer.legal.privacy': 'Política de Privacidad',
    'footer.legal.terms': 'Términos de Uso',
    'footer.legal.cookies': 'Uso de Cookies',
    'footer.copyright': '2026 Quimical es una marca de Empresa Brasileira de Mercadologia LTDA CNPJ 10.403.259/0001-37',
    'footer.version': 'Versión 1.1.9'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState, isLoading] = useLocalStorage<Language>('lang', 'pt', 'pt');

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['pt']] || key;
  };

  // Don't render until we've loaded the language from storage
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loading-skeleton w-32 h-8 rounded"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
